import React, { useState, useMemo, useEffect, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, Code, Lock, Star, AlertTriangle, Info, Minimize2, Maximize2, Loader2 } from 'lucide-react';
import { ScanFinding } from '../types/scan.ts';
import UpgradeButton from './UpgradeButton.tsx';
import { useFileContent } from '../hooks/useFileContent.ts';

interface CodePanelProps {
  fileName: string | null;
  findings: ScanFinding[];
  isProUser: boolean;
  onClose: () => void;
  onExpand?: () => void; // Handler for expand/collapse functionality
  isExpanded?: boolean; // Whether the panel is currently expanded
  className?: string;
  activeFinding?: ScanFinding; // Finding to highlight and scroll to
  scanId: string | null; // Added for real file content fetching
}

interface HighlightInfo {
  line: number;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  finding: ScanFinding;
}

export const CodePanel: React.FC<CodePanelProps> = ({
  fileName,
  findings,
  isProUser,
  onClose,
  onExpand,
  isExpanded,
  className = '',
  activeFinding,
  scanId
}) => {
  const [currentIssueIndex, setCurrentIssueIndex] = useState(0);
  const [isMinimized, setIsMinimized] = useState(false);
  const codeContainerRef = useRef<HTMLDivElement>(null);
  const [panelWidth, setPanelWidth] = useState(512); // Default width in px
  const panelRef = useRef<HTMLDivElement>(null);

  // Calculate maximum allowed width based on viewport and other panels
  const calculateMaxWidth = () => {
    const viewportWidth = window.innerWidth;
    const fileExplorerWidth = 320; // File explorer fixed width
    const aiAssistantWidth = 400; // AI assistant default width
    const scanResultsMinWidth = 400; // Minimum space for scan results
    return Math.max(320, viewportWidth - fileExplorerWidth - aiAssistantWidth - scanResultsMinWidth - 40); // 40px for borders/padding
  };

  // Use real file content hook (handles both real and demo modes internally)
  const { 
    text: fileContent, 
    loading: isLoading, 
    error: fileError, 
    isBinary 
  } = useFileContent(scanId, fileName);

  console.log('[CodePanel] Rendered with:', {
    fileName,
    findingsCount: findings.length,
    isProUser,
    currentIssueIndex,
    scanId,
    hasContent: !!fileContent,
    isLoading,
    hasError: !!fileError,
    isBinary
  });

  // Constrain initial panel width on mount
  useEffect(() => {
    const maxWidth = calculateMaxWidth();
    if (panelWidth > maxWidth) {
      setPanelWidth(maxWidth);
    }
  }, []);

  // Handle window resize to update max width constraints
  useEffect(() => {
    const handleResize = () => {
      const maxWidth = calculateMaxWidth();
      if (panelWidth > maxWidth) {
        setPanelWidth(maxWidth);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [panelWidth]);

  // Process findings to extract line highlights
  const highlights = useMemo<HighlightInfo[]>(() => {
    if (!findings.length) return [];
    
    return findings
      .filter(finding => finding.line && finding.line > 0)
      .map(finding => ({
        line: finding.line,
        severity: finding.severity as any,
        finding
      }))
      .sort((a, b) => a.line - b.line);
  }, [findings]);

  const currentHighlight = highlights[currentIssueIndex];

  // Sync currentIssueIndex with activeFinding when it changes
  useEffect(() => {
    if (activeFinding && highlights.length > 0) {
      const activeIndex = highlights.findIndex(h => h.finding.id === activeFinding.id);
      if (activeIndex !== -1) {
        setCurrentIssueIndex(activeIndex);
      }
    }
  }, [activeFinding, highlights]);

  // Auto-scroll to current highlighted line
  useEffect(() => {
    if (currentHighlight && codeContainerRef.current && fileContent) {
      // Calculate approximate line position (each line is ~32px including padding)
      const lineHeight = 32;
      const targetScrollTop = Math.max(0, (currentHighlight.line - 5) * lineHeight); // -5 to center it better
      
      // Use a small delay to ensure content is rendered
      const scrollTimer = setTimeout(() => {
        const container = codeContainerRef.current;
        if (container) {
          // Use type assertion to access scrollTop
          (container as any).scrollTop = targetScrollTop;
        }
      }, 150);
      
      return () => clearTimeout(scrollTimer);
    }
  }, [currentHighlight, fileContent]);

  // Navigation handlers
  const handlePrevIssue = () => {
    setCurrentIssueIndex(prev => 
      prev > 0 ? prev - 1 : highlights.length - 1
    );
  };

  const handleNextIssue = () => {
    setCurrentIssueIndex(prev => 
      prev < highlights.length - 1 ? prev + 1 : 0
    );
  };

  // Create highlighted content with line numbers
  const createHighlightedContent = () => {
    if (!fileContent) return [];
    
    const lines = fileContent.split('\n');
    const findingsByLine = new Map<number, ScanFinding>();
    
    findings.forEach(finding => {
      if (finding.line && finding.line > 0) {
        findingsByLine.set(finding.line, finding);
      }
    });

    return lines.map((line, index) => {
      const lineNumber = index + 1;
      const finding = findingsByLine.get(lineNumber);
      const isCurrentIssue = currentHighlight?.line === lineNumber;
      
      return {
        lineNumber,
        content: line,
        finding,
        isCurrentIssue
      };
    });
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
      case 'high':
        return 'bg-red-500/40 border-l-4 border-red-400';
      case 'medium':
        return 'bg-orange-500/40 border-l-4 border-orange-400';
      case 'low':
        return 'bg-yellow-500/40 border-l-4 border-yellow-400';
      default:
        return 'bg-blue-500/40 border-l-4 border-blue-400';
    }
  };

  const handleDrag = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = panelWidth;
    const onMouseMove = (moveEvent: globalThis.MouseEvent) => {
      const newWidth = startWidth + (moveEvent.clientX - startX);
      
      // Use the centralized max width calculation
      const maxAllowedWidth = calculateMaxWidth();
      
      setPanelWidth(Math.max(320, Math.min(newWidth, maxAllowedWidth)));
    };
    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  if (!fileName) {
    return null;
  }

  return (
    <div
      ref={panelRef}
      style={{ width: panelWidth, minWidth: 320 }}
      className="relative h-full flex flex-col border-r border-gray-700 bg-gray-900"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-gray-700 bg-gray-900">
        {!isMinimized && (
          <>
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <Code className="w-4 h-4 text-blue-400 flex-shrink-0" />
              <span className="text-sm font-medium text-white truncate" title={fileName}>
                {fileName.split('/').pop()}
              </span>
            </div>
            
            <div className="flex items-center gap-1">
              {highlights.length > 0 && (
                <div className="flex items-center gap-1 mr-2">
                  <button
                    onClick={handlePrevIssue}
                    className="p-1 hover:bg-gray-700 rounded text-gray-400 hover:text-white"
                    title="Previous issue"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-xs text-gray-400 px-1">
                    {currentIssueIndex + 1}/{highlights.length}
                  </span>
                  <button
                    onClick={handleNextIssue}
                    className="p-1 hover:bg-gray-700 rounded text-gray-400 hover:text-white"
                    title="Next issue"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
              
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="p-1 hover:bg-gray-700 rounded text-gray-400 hover:text-white"
                title={isMinimized ? "Expand panel" : "Minimize panel"}
              >
                {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
              </button>
              
              {onExpand && (
                <button
                  onClick={onExpand}
                  className="p-1 hover:bg-gray-700 rounded text-gray-400 hover:text-white"
                  title={isExpanded ? "Collapse code panel" : "Expand code panel"}
                >
                  {isExpanded ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.5 3.5L9 9M9 9V4.5M9 9H4.5M20.5 20.5L15 15M15 15v4.5M15 15h4.5" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.5 3.5M15 15v4.5M15 15h4.5M15 15l5.5 5.5" />
                    </svg>
                  )}
                </button>
              )}
              
              <button
                onClick={onClose}
                className="p-1 hover:bg-gray-700 rounded text-gray-400 hover:text-white"
                title="Close panel"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </>
        )}
        
        {isMinimized && (
          <button
            onClick={() => setIsMinimized(false)}
            className="p-2 hover:bg-gray-700 rounded text-gray-400 hover:text-white mx-auto"
            title="Expand panel"
          >
            <Code className="w-4 h-4" />
          </button>
        )}
      </div>

      {!isMinimized && (
        <>
          {/* Issue info */}
          {currentHighlight && (
            <div className="p-3 border-b border-gray-700 bg-gray-850">
              <IssueTooltip 
                finding={currentHighlight.finding} 
                isProUser={isProUser} 
                line={currentHighlight.line} 
              />
            </div>
          )}

          {/* Code display */}
          <div ref={codeContainerRef} className="flex-1 overflow-x-auto overflow-y-auto bg-gray-900">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="flex items-center gap-2 text-gray-400">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Loading file content...</span>
                </div>
              </div>
            ) : isBinary ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-gray-400 p-6">
                  <div className="text-gray-500 mb-2">📄</div>
                  <div className="font-medium">Binary file can't be previewed</div>
                  <div className="text-sm text-gray-500 mt-1">
                    This file contains binary data and cannot be displayed as text.
                  </div>
                </div>
              </div>
            ) : fileError ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-gray-400 p-6">
                  <div className="text-red-400 mb-2">⚠️</div>
                  <div className="font-medium">Failed to load file content</div>
                  <div className="text-sm text-gray-500 mt-1">
                    {fileError || 'Could not retrieve file content'}
                  </div>
                </div>
              </div>
            ) : fileContent ? (
              <div className="font-mono text-sm">
                {createHighlightedContent().map((lineData, index) => (
                  <div
                    key={index}
                    data-line={lineData.lineNumber}
                    className={`flex ${
                      lineData.finding 
                        ? `${getSeverityColor(lineData.finding.severity)}` 
                        : ''
                    } ${
                      lineData.isCurrentIssue ? 'bg-blue-500/50 border-r-4 border-blue-300' : ''
                    } hover:bg-gray-700/30`}
                  >
                    <div className="w-12 text-gray-500 text-right px-2 py-1 border-r border-gray-700 bg-gray-900/50 select-none">
                      {lineData.lineNumber}
                    </div>
                    <div className="flex-1 px-3 py-1 text-gray-300 whitespace-pre-wrap break-words min-w-0 overflow-x-auto">
                      {lineData.content || ' '}
                    </div>
                    {lineData.finding && (
                      <div className="px-2 py-1 flex items-center">
                        <AlertTriangle className="w-4 h-4 text-red-400" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-gray-400 p-6">
                  <div className="font-medium">No content available</div>
                  <div className="text-sm text-gray-500 mt-1">
                    Select a file to view its content
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
      {/* Drag handle for resizing */}
      <div
        style={{ cursor: 'ew-resize', width: 6, zIndex: 30, position: 'absolute', right: 0, top: 0, bottom: 0 }}
        className="bg-gray-800 hover:bg-blue-700 transition-colors duration-150"
        onMouseDown={handleDrag}
        aria-label="Resize code panel"
      />
    </div>
  );
};

// Issue tooltip component
const IssueTooltip: React.FC<{
  finding: ScanFinding;
  isProUser: boolean;
  line: number;
}> = ({ finding, isProUser, line }) => {
  const showDetails = isProUser || !finding.is_premium;

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
      case 'high':
        return <AlertTriangle className="w-4 h-4 text-red-400" />;
      case 'medium':
        return <AlertTriangle className="w-4 h-4 text-orange-400" />;
      case 'low':
        return <Info className="w-4 h-4 text-yellow-400" />;
      default:
        return <Info className="w-4 h-4 text-gray-400" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
      case 'high':
        return 'text-red-400 bg-red-900/20 border-red-700/50';
      case 'medium':
        return 'text-orange-400 bg-orange-900/20 border-orange-700/50';
      case 'low':
        return 'text-yellow-400 bg-yellow-900/20 border-yellow-700/50';
      default:
        return 'text-gray-400 bg-gray-900/20 border-gray-700/50';
    }
  };

  if (!showDetails) {
    return (
      <div className="p-3 bg-gray-800 border border-gray-700 rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <Lock className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-400">Premium Issue</span>
          {finding.is_premium && <Star className="w-4 h-4 text-yellow-400" />}
        </div>
        <p className="text-sm text-gray-400 mb-3">
          Upgrade to Pro to see detailed analysis for this issue.
        </p>
        <UpgradeButton />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-3">
        {getSeverityIcon(finding.severity)}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`px-2 py-1 text-xs font-medium rounded border ${getSeverityColor(finding.severity)}`}>
              {finding.severity.toUpperCase()}
            </span>
            {finding.is_premium && <Star className="w-4 h-4 text-yellow-400" />}
          </div>
          <h4 className="text-sm font-medium text-white mb-2 break-words">
            Line {line}: {finding.issue_type || 'Security Issue'}
          </h4>
          <p className="text-sm text-gray-300 mb-3 break-words leading-relaxed">
            {finding.message}
          </p>
          {finding.recommendation && (
            <div className="p-3 bg-blue-900/20 border border-blue-700/50 rounded-lg">
              <p className="text-xs text-blue-300 break-words leading-relaxed">
                <span className="font-medium">Recommendation:</span> {finding.recommendation}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CodePanel; 